import {
  ACTIVE_CHART_COLOR,
  graph_common_props,
  GRAPH_TOOLTIP_CLS,
  INACTIVE_CHART_COLOR,
} from '../../../../modules/consts/caf'

import { Bar, BarDatum, ComputedDatum } from '@nivo/bar'
import { useState } from 'react'
import { TDateISODate } from '../../../../model/caf.interface'
import { axisRange, maxAxisRange } from '../../../../modules/hooks/webview/caf/chart/utilFunction'

import { format, isDate } from 'date-fns'
import produce from 'immer'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useCafHighlightTagGraphDataV5 } from '../../../../modules/apis/webview/caf'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { ICON_BOOK_OPEN_GRAY600, ICON_CLOCK_GRAY, ICON_TUTOR_INACTIVE } from '../../../../modules/svg/svg'
import { COLORS } from '../../../../modules/vars'
import LoadingSpinner from '../../../common/LoadingSpinner'

const dummyDatePrefix = '2222-01-0'
interface IDataType extends BarDatum {
  start_time: string | TDateISODate
  color: string
  level: number
}

const createDataWithColor = (originalData: any[]) => {
  const emptyDataSet = (num) =>
    Array.from({ length: num }, (v, k) => ({
      start_time: `${dummyDatePrefix}${k}T00:00:00`,
      level: 0,
      color: 'transparent',
    }))
  let dataWithColor: IDataType[] = []
  dataWithColor = originalData?.map((d) => {
    return {
      ...d,
      start_time: d.start_time,
      level: Number(d.level),
      color: ACTIVE_CHART_COLOR,
      label: 1.5,
    }
  })
  if (originalData?.length < 6) {
    dataWithColor = dataWithColor.concat(emptyDataSet(6 - originalData.length) as any)
  }
  return dataWithColor
}

const StatBarGraph = ({ startDate, endDate, tag }) => {
  const isMobile = useIsMobile()
  const router = useRouter()

  const { data: graphData, isLoading } = useCafHighlightTagGraphDataV5(+tag, {
    startDate,
    endDate,
  })
  const [data, setData] = useState<IDataType[]>([])
  const handleClickBarItem = (e) => {
    setData(
      produce(data, (draft) => {
        const findIndex = draft?.findIndex((item) => item.start_time === e?.data.start_time)
        draft?.map((item) => (item.color = INACTIVE_CHART_COLOR))
        if (findIndex !== -1) {
          const targetBar = draft[findIndex]
          targetBar.color = ACTIVE_CHART_COLOR
        }
      })
    )
  }

  const levels = data?.map((item) => item.level) || []
  const maxValue = Math.max(...levels)
  const breakpoints = maxValue >= 24 ? 9 : maxValue >= 18 ? 6 : maxValue > 3 ? 3 : 1
  // static하게 maxYValue 정해진 경우는 해당 값으로 레인지 보여줌
  const yRange = axisRange(maxAxisRange(data, 'level'), breakpoints, 1)
  useEffect(() => {
    if (graphData?.highlights) {
      const next = createDataWithColor(
        graphData.highlights
          .map((item) => {
            return {
              ...item,
              level: item.count,
              tag: graphData.highlight_tag,
            }
          })
          .map((item) => {
            return { ...item, label: breakpoints }
          })
      )
      setData(next)
    }
  }, [graphData])

  if (isMobile) {
    return (
      <div className={styles.middle.box}>
        <Bar
          data={data}
          tooltip={ToolTip}
          onClick={(e: ComputedDatum<{ start_time: string; level: number; color: string }>) => handleClickBarItem(e)}
          indexBy={'start_time'}
          keys={['level', 'label']}
          colors={(props) => {
            if (props.id === 'level') {
              return props.data.color
            }
            return 'rgba(255,0,0,0.0)'
          }}
          colorBy={'indexValue'}
          gridYValues={yRange}
          ariaLabel="bar-chart"
          maxValue={'auto'}
          role="application"
          margin={{ bottom: 20, left: 20, top: 20 }}
          axisLeft={{
            tickSize: 0,
            tickValues: yRange,
            tickPadding: 10,
          }}
          theme={graph_common_props}
          layers={['grid', 'bars', 'axes']}
          borderRadius={2}
          padding={16 / 65}
          axisBottom={{
            tickSize: 0,
            legendPosition: 'start',
            renderTick: (tick) => <CustomTick tick={tick} color={COLORS.GRAY300} />,
          }}
          label={(d) => {
            if (d.id === 'label' && data[d.index] && data[d.index]['level'] > 0) return data[d.index]['level'] as any
          }}
          labelTextColor={COLORS.BLACK}
          height={160}
          width={data.length * 48}
        />
      </div>
    )
  } else {
    return isLoading ? (
      <div className="flex h-[231px] flex-row items-center justify-center">
        <LoadingSpinner show primary lg />
      </div>
    ) : (
      <div className={styles.middle.box_pc}>
        <Bar
          data={data}
          tooltip={ToolTip}
          onClick={(e: ComputedDatum<{ start_time: string; level: number; color: string }>) => handleClickBarItem(e)}
          indexBy={'start_time'}
          keys={['level', 'label']}
          colors={(props) => {
            if (props.id === 'level') {
              return props.data.color
            }
            return 'rgba(255,0,0,0.0)'
          }}
          colorBy={'indexValue'}
          gridYValues={yRange}
          ariaLabel="bar-chart"
          maxValue={'auto'}
          role="application"
          margin={{ bottom: 20, left: 20, top: 20 }}
          axisLeft={{
            tickSize: 0,
            tickValues: yRange,
            tickPadding: 10,
          }}
          theme={graph_common_props}
          layers={['grid', 'bars', 'axes']}
          borderRadius={2}
          padding={16 / 65}
          axisBottom={{
            tickSize: 0,
            legendPosition: 'start',
            renderTick: (tick) => <CustomTick tick={tick} color={COLORS.GRAY300} fontSize={14} />,
          }}
          label={(d) => {
            if (d.id === 'label' && data[d.index] && data[d.index]['level'] > 0) return data[d.index]['level'] as any
          }}
          labelTextColor={COLORS.BLACK}
          height={231}
          width={data.length * 48}
        />
      </div>
    )
  }
}

export default StatBarGraph

const styles = {
  middle: {
    box: `mt-[12px] h-[160px] w-full overflow-x-scroll bg-white`,
    box_pc: `mt-[12px] h-[231px] w-full overflow-x-scroll bg-white`,
  },
}

const CustomTick = ({ tick, color, fontSize = 12 }) => {
  const formatted = tick?.value?.includes(dummyDatePrefix)
    ? ''
    : isDate(new Date(tick.value))
    ? format(new Date(tick.value), 'M.d')
    : ''
  return (
    <g transform={`translate(${tick.x},${tick.y + 12})`}>
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fill: color,
          fontSize: fontSize,
        }}
      >
        {formatted}
      </text>
    </g>
  )
}
const ToolTip = ({ data }) => {
  return (
    <div className={GRAPH_TOOLTIP_CLS}>
      <div className="flex items-center gap-[8px] mb-[4px]">
        <img src={ICON_CLOCK_GRAY} className="w-[20px]" alt="Clock" />
        <span className="text-h6 font-medium text-black">{data.formatted_start_time}</span>
      </div>
      <div className="flex items-center gap-[8px] mb-[4px]">
        <img src={ICON_TUTOR_INACTIVE} className="w-[20px]" alt="Tutor Inactive" />
        <span className="text-h6 font-medium text-black">{data.tutor_first_name}</span>
      </div>
      <div className="flex items-center gap-[8px] mb-[4px]">
        <img src={ICON_BOOK_OPEN_GRAY600} className="w-[20px]" alt="Book" />
        <span className="text-h6 w-[120px] truncate font-medium text-black">{data.material}</span>
      </div>
    </div>
  )
}
